import React from "react";
import { Dropdown } from "react-bootstrap";
import { Link } from "react-router-dom";
import { Row } from "@tanstack/react-table";

import { Download, EllipsisVertical, Pencil, Trash, X } from "lucide-react";
import { type Ticket } from "Stores/dashboardStore/tickets";
import { Utils } from "@/common/utils";

interface TableMenuProps {
    isOpen: boolean;
    toggleDropdown: (rowId: string | null) => void;
    row: Row<Ticket>;
    onDownloadPlan: (id: string) => void;
    onDeletePlan: (ticket: Ticket) => void;
    onToggleStatus?: (ticket: Ticket) => void;
    dashboardId?: number;
}

export default function TableMenu({ isOpen, row, onDeletePlan, onDownloadPlan, onToggleStatus, toggleDropdown, dashboardId }: TableMenuProps) {
    const isEditVisible: boolean = Utils.hasAdminAccess() || row.original.permissions.owner || row.original.permissions.assignee;
    const getPlanUrl = row.original.dashboard_id ? Utils.getActionplanUrl(row.original.dashboard, row.original.ruuid) : "";

    const isSameDashboard = row.original.dashboard_id === dashboardId;

    // Check if ActionPlan has a due date
    const hasDueDate = row.original.evaluation_date && 
        row.original.evaluation_date !== "" && 
        row.original.evaluation_date !== null;

    // Show "Close ActionPlan" button for tasks WITH due dates that are open
    const canToggleStatus = row.original.type === "task" &&
        row.original.permissions.can_edit_status &&
        hasDueDate &&
        row.original.status !== "closed";

    // Show "Resolve ActionPlan" button for ANY ActionPlan WITHOUT due dates that is not closed
    // This includes:
    // 1. Observations (type === "observation") - they never have due dates
    // 2. Tasks that had their due date removed (type === "task" but no evaluation_date)
    const canResolveWithoutDueDate = row.original.permissions.can_edit_status &&
        !hasDueDate &&
        row.original.status !== "closed";

    return (
        <Dropdown show={isOpen} onToggle={(isOpen, e) => {
            e.originalEvent?.preventDefault(),
                e.originalEvent?.stopPropagation(),
                toggleDropdown(row.id);
        }}
            className="table-options"
        >
            <Dropdown.Toggle tabIndex={0} className="toggle d-flex align-items-center">
                <div className="trailing-icon">
                    <EllipsisVertical size={20} />
                </div>
            </Dropdown.Toggle>

            <Dropdown.Menu>
                <Dropdown.Item as={"button"} className="d-flex align-items-center gap-2" onClick={() => onDownloadPlan(row.original.ruuid)}>
                    <span>
                        <Download />
                    </span>
                    Download
                </Dropdown.Item>

                {!isSameDashboard && isEditVisible ? (
                    <Dropdown.Item
                        as="button"
                        id={`ap-edit-${row.original.ruuid}`}
                        className="d-flex align-items-center gap-2"
                        onClick={() => { toggleDropdown(row.id); row.getToggleExpandedHandler()(); }}
                    >
                        <span><Pencil /></span>
                        {getPlanUrl ? (
                            <Link to={getPlanUrl}>Edit Details</Link>
                        ) : (
                            <button className="bg-transparent border-0" onClick={() => toggleDropdown(row.id)}>
                                Edit Details
                            </button>
                        )}
                    </Dropdown.Item>
                ) : null}

                {canToggleStatus && onToggleStatus ? (
                    <Dropdown.Item as={"button"} className="d-flex align-items-center gap-2" onClick={() => onToggleStatus(row.original)}>
                        <span>
                            <X />
                        </span>
                        Close ActionPlan
                    </Dropdown.Item>
                ) : null}

                {canResolveWithoutDueDate && onToggleStatus ? (
                    <Dropdown.Item as={"button"} className="d-flex align-items-center gap-2" onClick={() => onToggleStatus(row.original)}>
                        <span>
                            <X />
                        </span>
                        Resolve ActionPlan
                    </Dropdown.Item>
                ) : null}

                {Utils.isSuperAdmin() || Utils.hasAdminAccess() ? (
                    <Dropdown.Item as={"button"} className="d-flex align-items-center gap-2" onClick={() => onDeletePlan(row.original)}>
                        <span>
                            <Trash />
                        </span>
                        Delete
                    </Dropdown.Item>
                ) : null}
            </Dropdown.Menu>
        </Dropdown>
    );
}
